<template>
    <breeze-authenticated-layout>
        <h2 class="font-semibold text-xl text-gray-800 leading-tight">
            {{
                $t(
                    "questionnairesCreate.Create a Heat Pump Selection Questionnaire"
                )
            }}
        </h2>

        <div class="py-2 mt-3">
            <div class="max-w-full mx-auto sm:px-6 lg:px-8">
                <div class="bg-white overflow-hidden shadow-sm sm:rounded-lg">
                    <form @submit.prevent="store">
                        <div class="flex flex-wrap -mb-8 -mr-6 p-8">
                            <div class="lg:w-1/4">
                                <h3>
                                    {{ $t("questionnairesCreate.Building") }}
                                </h3>
                                <text-input
                                    v-model="form.year_built"
                                    :error="form.errors.year_built"
                                    type="number"
                                    class="pb-2 pr-5 w-full"
                                    :label="
                                        $t('questionnairesCreate.Year of Build')
                                    "
                                />
                                <select-input
                                    v-model="form.building_type"
                                    :error="form.errors.building_type"
                                    class="pb-2 pr-5 w-full"
                                    :label="
                                        $t('questionnairesCreate.Building type')
                                    "
                                >
                                    <option :value="null" disabled />
                                    <option value="Dom jednorodzinny">
                                        {{
                                            $t(
                                                "questionnairesCreate.Single-family house"
                                            )
                                        }}
                                    </option>
                                    <option value="Bliźniak">
                                        {{ $t("questionnairesCreate.Twin") }}
                                    </option>
                                    <option value="Zabudowa szeregowa">
                                        {{
                                            $t(
                                                "questionnairesCreate.Terraced houses"
                                            )
                                        }}
                                    </option>
                                    <option value="Mieszkanie">
                                        {{
                                            $t("questionnairesCreate.Apartment")
                                        }}
                                    </option>
                                </select-input>
                                <text-input
                                    v-model="form.total_footage"
                                    :error="form.errors.total_footage"
                                    type="number"
                                    class="pb-2 pr-5 w-full"
                                    :label="
                                        $t(
                                            'questionnairesCreate.Total area m2'
                                        )
                                    "
                                />
                                <text-input
                                    v-model="form.used_footage"
                                    :error="form.errors.used_footage"
                                    type="number"
                                    class="pb-2 pr-5 w-full"
                                    :label="
                                        $t(
                                            'questionnairesCreate.Usable area m2'
                                        )
                                    "
                                />
                                <text-input
                                    v-model="form.used_cubature"
                                    :error="form.errors.used_cubature"
                                    class="pb-2 pr-5 w-full"
                                    type="number"
                                    :is-required="false"
                                    :label="
                                        $t(
                                            'questionnairesCreate.Usable area m3'
                                        )
                                    "
                                />
                                <text-input
                                    v-model="form.floors"
                                    :error="form.errors.floors"
                                    type="number"
                                    class="pb-2 pr-5 w-full"
                                    :label="
                                        $t(
                                            'questionnairesCreate.Number of storeys'
                                        )
                                    "
                                />
                                <text-input
                                    v-model="form.floor_used_for_hp"
                                    :error="form.errors.floor_used_for_hp"
                                    type="number"
                                    class="pb-2 pr-5 w-full"
                                    :label="
                                        $t(
                                            'questionnairesCreate.Heat pump installation storey'
                                        )
                                    "
                                />
                                <text-input
                                    v-model="form.habitants_number"
                                    :error="form.errors.habitants_number"
                                    type="number"
                                    class="pb-2 pr-5 w-full"
                                    :label="
                                        $t(
                                            'questionnairesCreate.Number of people living in the building'
                                        )
                                    "
                                />
                                <select-input
                                    v-model="form.ventilation_type"
                                    :error="form.errors.ventilation_type"
                                    class="pb-2 pr-5 w-full"
                                    :label="
                                        $t(
                                            'questionnairesCreate.Type of ventilation'
                                        )
                                    "
                                >
                                    <option :value="null" disabled />
                                    <option value="Grawitacyjna">
                                        {{
                                            $t(
                                                "questionnairesCreate.Gravitational"
                                            )
                                        }}
                                    </option>
                                    <option value="Rekuperacja">
                                        {{
                                            $t(
                                                "questionnairesCreate.Recuperation"
                                            )
                                        }}
                                    </option>
                                </select-input>
                                <checkbox-input
                                    v-model="form.is_canadian"
                                    :error="form.errors.is_canadian"
                                    class="mt-2 pb-2 pr-5 w-full"
                                    :label="
                                        $t(
                                            'questionnairesCreate.Frame house Canadian'
                                        )
                                    "
                                />
                                <checkbox-input
                                    v-model="form.has_basement"
                                    :error="form.errors.has_basement"
                                    class="mt-2 pb-2 pr-5 w-full"
                                    :label="
                                        $t(
                                            'questionnairesCreate.Object with a basement'
                                        )
                                    "
                                />
                                <checkbox-input
                                    v-model="form.has_garage"
                                    :error="form.errors.has_garage"
                                    class="mt-2 pb-2 pr-5 w-full"
                                    :label="
                                        $t(
                                            'questionnairesCreate.Object with a garage in a block of the building'
                                        )
                                    "
                                />
                            </div>
                            <div class="lg:w-1/4">
                                <h3>
                                    {{ $t("questionnairesCreate.Insulation") }}
                                </h3>
                                <select-input
                                    v-model="form.wall_material"
                                    :error="form.errors.wall_material"
                                    class="pb-2 pr-5 w-full"
                                    :label="
                                        $t(
                                            'questionnairesCreate.Basic building material'
                                        )
                                    "
                                >
                                    <option :value="null" disabled />
                                    <option value="Beton">
                                        {{
                                            $t("questionnairesCreate.Concrete")
                                        }}
                                    </option>
                                    <option value="Beton komórkowy">
                                        {{
                                            $t(
                                                "questionnairesCreate.Aerated concrete"
                                            )
                                        }}
                                    </option>
                                    <option value="Bloczek silikatowy">
                                        {{
                                            $t(
                                                "questionnairesCreate.Silicate block"
                                            )
                                        }}
                                    </option>
                                    <option value="Cegła dziurawka">
                                        {{
                                            $t(
                                                "questionnairesCreate.Hollow brick"
                                            )
                                        }}
                                    </option>
                                    <option value="Cegła pełna">
                                        {{
                                            $t(
                                                "questionnairesCreate.Brick full"
                                            )
                                        }}
                                    </option>
                                    <option value="Drewno">
                                        {{ $t("questionnairesCreate.Wood") }}
                                    </option>
                                    <option value="Keramzytobeton">
                                        {{
                                            $t(
                                                "questionnairesCreate.Expanded clay concrete"
                                            )
                                        }}
                                    </option>
                                    <option value="Porotherm">
                                        {{
                                            $t("questionnairesCreate.Porotherm")
                                        }}
                                    </option>
                                    <option value="Pustak keramzytowy">
                                        {{
                                            $t(
                                                "questionnairesCreate.LECA block"
                                            )
                                        }}
                                    </option>
                                    <option value="Pustaki ceramiczne">
                                        {{
                                            $t(
                                                "questionnairesCreate.Ceramic blocks"
                                            )
                                        }}
                                    </option>
                                    <option value="Ytong">
                                        {{ $t("questionnairesCreate.Ytong") }}
                                    </option>
                                    <option value="Piaskowiec">
                                        {{
                                            $t("questionnairesCreate.Sandstone")
                                        }}
                                    </option>
                                </select-input>
                                <text-input
                                    v-model="form.wall_width"
                                    :error="form.errors.wall_width"
                                    type="number"
                                    class="pb-2 pr-5 w-full"
                                    :label="
                                        $t(
                                            'questionnairesCreate.Wall thickness without insulation cm'
                                        )
                                    "
                                />
                                <select-input
                                    v-model="form.wall_insulation_material"
                                    :error="
                                        form.errors.wall_insulation_material
                                    "
                                    class="pb-2 pr-5 w-full"
                                    :label="
                                        $t(
                                            'questionnairesCreate.Wall insulation material'
                                        )
                                    "
                                >
                                    <option :value="null" disabled />
                                    <option value="Brak">
                                        {{
                                            $t("questionnairesCreate.Concrete")
                                        }}
                                    </option>
                                    <option value="PIR">
                                        {{ $t("questionnairesCreate.PIR") }}
                                    </option>
                                    <option value="PUR">
                                        {{ $t("questionnairesCreate.PUR") }}
                                    </option>
                                    <option value="Styropian">
                                        {{
                                            $t("questionnairesCreate.Styrofoam")
                                        }}
                                    </option>
                                    <option value="Styropian grafitowy">
                                        {{
                                            $t(
                                                "questionnairesCreate.Graphite polystyrene"
                                            )
                                        }}
                                    </option>
                                    <option value="Styropian twardy (XPS)">
                                        {{
                                            $t(
                                                "questionnairesCreate.Hard polystyrene (XPS)"
                                            )
                                        }}
                                    </option>
                                    <option value="Wełna drzewna">
                                        {{
                                            $t("questionnairesCreate.Wood wool")
                                        }}
                                    </option>
                                    <option value="Wełna mineralna">
                                        {{
                                            $t(
                                                "questionnairesCreate.Mineral wool"
                                            )
                                        }}
                                    </option>
                                </select-input>
                                <text-input
                                    v-model="form.wall_insulation_width"
                                    :error="form.errors.wall_insulation_width"
                                    type="number"
                                    class="pb-2 pr-5 w-full"
                                    :label="
                                        $t(
                                            'questionnairesCreate.Wall insulation thickness'
                                        )
                                    "
                                />
                                <select-input
                                    v-model="form.roof_insulation_material"
                                    :error="
                                        form.errors.roof_insulation_material
                                    "
                                    class="pb-2 pr-5 w-full"
                                    :label="
                                        $t(
                                            'questionnairesCreate.Roof insulation material'
                                        )
                                    "
                                >
                                    <option :value="null" disabled />
                                    <option value="Brak">
                                        {{
                                            $t("questionnairesCreate.Concrete")
                                        }}
                                    </option>
                                    <option value="Ekofiber">
                                        {{
                                            $t("questionnairesCreate.Ekofiber")
                                        }}
                                    </option>
                                    <option value="Korek">
                                        {{ $t("questionnairesCreate.Cork") }}
                                    </option>
                                    <option value="PIR">
                                        {{ $t("questionnairesCreate.PIR") }}
                                    </option>
                                    <option value="PUR">
                                        {{ $t("questionnairesCreate.PUR") }}
                                    </option>
                                    <option value="Styropian">
                                        {{
                                            $t("questionnairesCreate.Styrofoam")
                                        }}
                                    </option>
                                    <option value="Styropian grafitowy">
                                        {{
                                            $t(
                                                "questionnairesCreate.Graphite polystyrene"
                                            )
                                        }}
                                    </option>
                                    <option value="Styropian twardy (XPS)">
                                        {{
                                            $t(
                                                "questionnairesCreate.Hard polystyrene (XPS)"
                                            )
                                        }}
                                    </option>
                                    <option value="Wełna drzewna">
                                        {{
                                            $t("questionnairesCreate.Wood wool")
                                        }}
                                    </option>
                                    <option value="Wełna mineralna">
                                        {{
                                            $t(
                                                "questionnairesCreate.Mineral wool"
                                            )
                                        }}
                                    </option>
                                    <option value="Wełna mineralna granulowana">
                                        {{
                                            $t(
                                                "questionnairesCreate.Granulated mineral wool"
                                            )
                                        }}
                                    </option>
                                </select-input>
                                <text-input
                                    v-model="form.roof_insulation_width"
                                    :error="form.errors.roof_insulation_width"
                                    type="number"
                                    class="pb-2 pr-5 w-full"
                                    :label="
                                        $t(
                                            'questionnairesCreate.Roof insulation thickness cm'
                                        )
                                    "
                                />
                                <select-input
                                    v-model="form.floor_insulation_material"
                                    :error="
                                        form.errors.floor_insulation_material
                                    "
                                    class="pb-2 pr-5 w-full"
                                    :label="
                                        $t(
                                            'questionnairesCreate.The material of the insulation of the ground floor'
                                        )
                                    "
                                >
                                    <option :value="null" disabled />
                                    <option value="Brak">
                                        {{
                                            $t("questionnairesCreate.Concrete")
                                        }}
                                    </option>
                                    <option value="Ekofiber">
                                        {{
                                            $t("questionnairesCreate.Ekofiber")
                                        }}
                                    </option>
                                    <option value="Korek">
                                        {{ $t("questionnairesCreate.Cork") }}
                                    </option>
                                    <option value="PIR">
                                        {{ $t("questionnairesCreate.PIR") }}
                                    </option>
                                    <option value="PUR">
                                        {{ $t("questionnairesCreate.PUR") }}
                                    </option>
                                    <option value="Styropian">
                                        {{
                                            $t("questionnairesCreate.Styrofoam")
                                        }}
                                    </option>
                                    <option value="Styropian grafitowy">
                                        {{
                                            $t(
                                                "questionnairesCreate.Graphite polystyrene"
                                            )
                                        }}
                                    </option>
                                    <option value="Styropian twardy (XPS)">
                                        {{
                                            $t(
                                                "questionnairesCreate.Hard polystyrene (XPS)"
                                            )
                                        }}
                                    </option>
                                    <option value="Wełna drzewna">
                                        {{
                                            $t("questionnairesCreate.Wood wool")
                                        }}
                                    </option>
                                    <option value="Wełna mineralna">
                                        {{
                                            $t(
                                                "questionnairesCreate.Mineral wool"
                                            )
                                        }}
                                    </option>
                                    <option value="Wełna mineralna granulowana">
                                        {{
                                            $t(
                                                "questionnairesCreate.Granulated mineral wool"
                                            )
                                        }}
                                    </option>
                                </select-input>
                                <text-input
                                    v-model="form.floor_insulation_width"
                                    :error="form.errors.floor_insulation_width"
                                    type="number"
                                    class="pb-2 pr-5 w-full"
                                    :label="
                                        $t(
                                            'questionnairesCreate.The thickness of the insulation on the ground floor'
                                        )
                                    "
                                />
                                <checkbox-input
                                    v-model="form.wall_insulated_inside"
                                    :error="form.errors.wall_insulated_inside"
                                    class="mt-2 pb-2 pr-5 w-full"
                                    :label="
                                        $t(
                                            'questionnairesCreate.The building is insulated from the inside'
                                        )
                                    "
                                />
                            </div>
                            <div class="lg:w-1/4">
                                <h3>
                                    {{ $t("questionnairesCreate.Glazing") }}
                                </h3>
                                <text-input
                                    v-model="form.windows_number_three_pane"
                                    :error="
                                        form.errors.windows_number_three_pane
                                    "
                                    type="number"
                                    class="pb-2 pr-5 w-full"
                                    :label="
                                        $t(
                                            'questionnairesCreate.Number of triple-glazed windows'
                                        )
                                    "
                                />
                                <text-input
                                    v-model="form.windows_number_two_pane"
                                    :error="form.errors.windows_number_two_pane"
                                    type="number"
                                    class="pb-2 pr-5 w-full"
                                    :label="
                                        $t(
                                            'questionnairesCreate.Number of double-glazed windows'
                                        )
                                    "
                                />
                                <text-input
                                    v-model="form.glass_door_number_three_pane"
                                    :error="
                                        form.errors.glass_door_number_three_pane
                                    "
                                    type="number"
                                    class="pb-2 pr-5 w-full"
                                    :label="
                                        $t(
                                            'questionnairesCreate.Number of triple-glazed patio doors'
                                        )
                                    "
                                />
                                <text-input
                                    v-model="form.glass_door_number_two_pane"
                                    :error="
                                        form.errors.glass_door_number_two_pane
                                    "
                                    type="number"
                                    class="pb-2 pr-5 w-full"
                                    :label="
                                        $t(
                                            'questionnairesCreate.Number of double-glazed patio doors'
                                        )
                                    "
                                />
                                <text-input
                                    v-model="form.big_windows_number_three_pane"
                                    :error="
                                        form.errors
                                            .big_windows_number_three_pane
                                    "
                                    type="number"
                                    class="pb-2 pr-5 w-full"
                                    :label="
                                        $t(
                                            'questionnairesCreate.Large triple glazing'
                                        )
                                    "
                                />
                                <text-input
                                    v-model="form.big_windows_number_two_pane"
                                    :error="
                                        form.errors.big_windows_number_two_pane
                                    "
                                    type="number"
                                    class="pb-2 pr-5 w-full"
                                    :label="
                                        $t(
                                            'questionnairesCreate.Large double glazing'
                                        )
                                    "
                                />
                            </div>
                            <div class="lg:w-1/4">
                                <h3>
                                    {{ $t("questionnairesCreate.Heating") }}
                                </h3>
                                <text-input
                                    v-model="form.temperature_required"
                                    :error="form.errors.temperature_required"
                                    type="number"
                                    class="pb-2 pr-5 w-full"
                                    :label="
                                        $t(
                                            'questionnairesCreate.Required temperature c'
                                        )
                                    "
                                />
                                <text-input
                                    v-model="form.temperature_past_winter"
                                    :error="form.errors.temperature_past_winter"
                                    type="number"
                                    class="pb-2 pr-5 w-full"
                                    :label="
                                        $t(
                                            'questionnairesCreate.Temperature in the last heating season'
                                        )
                                    "
                                />
                                <select-input
                                    v-model="form.house_heating_fuel_id"
                                    :error="form.errors.house_heating_fuel_id"
                                    class="pb-2 pr-5 w-full"
                                    :label="
                                        $t(
                                            'questionnairesCreate.Current source of heating'
                                        )
                                    "
                                >
                                    <option :value="null" disabled />
                                    <option
                                        v-for="fuel in houseHeatingFuels"
                                        :value="fuel.id"
                                    >
                                        {{ fuel.name }}
                                    </option>
                                </select-input>
                                <text-input
                                    v-model="form.fuel_used"
                                    :error="form.errors.fuel_used"
                                    type="number"
                                    class="pb-2 pr-5 w-full"
                                    :label="
                                        $t(
                                            'questionnairesCreate.Amount of fuel consumed l m t'
                                        )
                                    "
                                />
                                <text-input
                                    v-model="form.ceilings_height"
                                    :error="form.errors.ceilings_height"
                                    type="number"
                                    class="pb-2 pr-5 w-full"
                                    :label="
                                        $t(
                                            'questionnairesCreate.Room height cm'
                                        )
                                    "
                                />
                                <text-input
                                    v-model="form.floor_heating_area"
                                    :error="form.errors.floor_heating_area"
                                    type="number"
                                    class="pb-2 pr-5 w-full"
                                    :label="
                                        $t(
                                            'questionnairesCreate.Area heated by the floor m2'
                                        )
                                    "
                                />
                                <text-input
                                    v-model="form.radiators_area"
                                    :error="form.errors.radiators_area"
                                    type="number"
                                    class="pb-2 pr-5 w-full"
                                    :label="
                                        $t(
                                            'questionnairesCreate.Area heated by radiators m2'
                                        )
                                    "
                                />

                                <text-input
                                    v-model="form.radiators_number"
                                    :error="form.errors.radiators_number"
                                    type="number"
                                    class="pb-2 pr-5 w-full"
                                    :label="$t('questionnairesCreate.radiators_number')"
                                />

                                <text-input
                                    v-model="form.ceiling_height_hp_room"
                                    :error="form.errors.ceiling_height_hp_room"
                                    type="number"
                                    class="pb-2 pr-5 w-full"
                                    :label="
                                        $t(
                                            'questionnairesCreate.Room height at the heat pump installation site cm'
                                        )
                                    "
                                />
                                <text-input
                                    v-model="form.door_width_hp_room"
                                    :error="form.errors.door_width_hp_room"
                                    type="number"
                                    class="pb-2 pr-5 w-full"
                                    :label="
                                        $t(
                                            'questionnairesCreate.The width of the door where the heat pump is installed'
                                        )
                                    "
                                />
                            </div>
                            <div class="w-full my-6">
                                <textarea-input
                                    v-model="form.notes"
                                    :error="form.errors.notes"
                                    class="w-full"
                                    :label="$t('questionnairesCreate.Notes')"
                                />
                            </div>
                            <div class="lg:w-full">
                                <div
                                    class="flex items-center justify-center px-8 py-4 border-t border-gray-100"
                                >
                                    <loading-button
                                        :loading="form.processing"
                                        class="btn-indigo"
                                        type="submit"
                                        >{{
                                            $t("questionnairesCreate.Save")
                                        }}</loading-button
                                    >
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    </breeze-authenticated-layout>
</template>

<script>
import BreezeAuthenticatedLayout from "@/Layouts/Authenticated";
import TextInput from "@/Components/Shared/TextInput";
import CheckboxInput from "@/Components/Shared/CheckboxInput";
import SelectInput from "@/Components/Shared/SelectInput";
import LoadingButton from "@/Components/Shared/LoadingButton";
import TextareaInput from "@/Components/Shared/TextareaInput";

export default {
    props: {
        customer: {
            type: Object,
            required: true,
        },
        houseHeatingFuels: {
            type: Array,
            required: true,
        },
    },
    components: {
        BreezeAuthenticatedLayout,
        LoadingButton,
        SelectInput,
        TextInput,
        CheckboxInput,
        TextareaInput,
    },
    remember: "form",
    data() {
        return {
            form: this.$inertia.form({
                customer_id: this.customer.id,
                user_id: this.$page.props.auth.user.id,
                year_built: null,
                building_type: null,
                is_canadian: null,
                total_footage: null,
                used_footage: null,
                used_cubature: null,
                has_basement: null,
                has_garage: null,
                floors: null,
                floor_used_for_hp: null,
                habitants_number: null,
                ventilation_type: null,
                wall_material: null,
                wall_width: null,
                wall_insulation_material: null,
                wall_insulation_width: null,
                wall_insulated_inside: null,
                roof_insulation_material: null,
                roof_insulation_width: null,
                floor_insulation_material: null,
                floor_insulation_width: null,
                windows_number_three_pane: null,
                windows_number_two_pane: null,
                glass_door_number_three_pane: null,
                glass_door_number_two_pane: null,
                big_windows_number_three_pane: null,
                big_windows_number_two_pane: null,
                temperature_required: null,
                temperature_past_winter: null,
                house_heating_fuel_id: null,
                fuel_used: null,
                floor_heating_area: null,
                radiators_area: null,
                ceilings_height: null,
                ceiling_height_hp_room: null,
                door_width_hp_room: null,
                extra_info: null,
                notes: null,
                radiators_number: null,
            }),
        };
    },
    methods: {
        store() {
            this.form.post(
                `/customers/${this.customer.id}/questionnaire/store`
            );
        },
    },
};
</script>
